import * as React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { postJSON } from '@/lib/api'
import { displayDate } from '@/lib/dates'

export interface OpenInvoice {
  id: number
  number: number
  origin: string
  status: string
  due_on: string
  balance: string
  balance_cents: number
}

interface CheckoutResponse {
  checkout_session: { checkout_url: string }
}

interface InvoiceSelectionQuoteResponse {
  quote: {
    invoice_ids: number[]
    invoice_count: number
    amount_cents: number
    amount: string
    currency: string
  }
}

export function MemberPortalOpenInvoices({
  invoices,
  onOpen,
  heading = 'Open invoices',
  description,
  secondaryAction = false,
  initiallySelectedIds,
}: {
  invoices: OpenInvoice[]
  onOpen: (invoice: OpenInvoice) => void
  heading?: string
  description?: string
  secondaryAction?: boolean
  initiallySelectedIds?: number[]
}) {
  const [selected, setSelected] = React.useState(
    () => new Set(initiallySelectedIds ?? invoices.map((invoice) => invoice.id)),
  )
  const idempotencyKey = React.useRef(`portal-pay-${crypto.randomUUID()}`)
  const selectedInvoices = invoices.filter((invoice) => selected.has(invoice.id))
  const selectedInvoiceIds = selectedInvoices
    .map((invoice) => invoice.id)
    .sort((left, right) => left - right)
  const quote = useQuery({
    queryKey: ['member-portal-invoice-quote', selectedInvoiceIds],
    queryFn: () =>
      postJSON<InvoiceSelectionQuoteResponse>('/api/v1/member_portal/billing/quote', {
        payment: { invoice_ids: selectedInvoiceIds },
      }),
    retry: false,
  })
  const checkout = useMutation({
    mutationFn: () => {
      if (!quote.data?.quote) throw new Error('Invoice quote is unavailable')

      return postJSON<CheckoutResponse>('/api/v1/member_portal/billing/checkout', {
        payment: {
          invoice_ids: quote.data.quote.invoice_ids,
          quoted_amount_cents: quote.data.quote.amount_cents,
          idempotency_key: idempotencyKey.current,
        },
      })
    },
    onSuccess: (data) => window.location.assign(data.checkout_session.checkout_url),
  })

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-body font-semibold text-foreground">{heading}</h2>
        {description && <p className="mt-1 text-caption text-muted-foreground">{description}</p>}
      </div>
      {checkout.isError && <Alert>We couldn’t open checkout. Please try again.</Alert>}
      {quote.isError && <Alert>We couldn’t update the selected Invoice total.</Alert>}
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <div className="divide-y divide-border">
          {invoices.map((invoice) => (
            <div
              key={invoice.id}
              className={`flex min-h-9 items-center gap-3 px-4 py-2 ${
                selected.has(invoice.id) ? 'bg-primary/5' : ''
              }`}
            >
              <Checkbox
                checked={selected.has(invoice.id)}
                onCheckedChange={() => toggleSelection(invoice.id)}
                aria-label={`Select Invoice ${invoice.number}`}
              />
              <button
                type="button"
                onClick={() => onOpen(invoice)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-body-sm font-medium">
                    Invoice {invoice.number} · {invoice.origin}
                  </span>
                  <span className="block text-caption text-muted-foreground">
                    Due {displayDate(invoice.due_on)}
                  </span>
                </span>
                <span className="shrink-0 text-right text-body-sm font-medium tabular-nums">
                  {invoice.balance}
                </span>
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-border px-4 py-3">
          <span className="text-body-sm text-muted-foreground">
            {selected.size} selected · <span className="tabular-nums">{quoteLabel(quote)}</span>
          </span>
          <Button
            type="button"
            variant={secondaryAction ? 'outline' : 'default'}
            disabled={
              selected.size === 0 || checkout.isPending || quote.isFetching || !quote.data?.quote
            }
            onClick={() => checkout.mutate()}
          >
            {checkout.isPending
              ? 'Opening checkout...'
              : quote.data?.quote
                ? `Pay ${quote.data.quote.amount}`
                : 'Updating total...'}
          </Button>
        </div>
      </div>
    </section>
  )

  function toggleSelection(invoiceId: number) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(invoiceId)) next.delete(invoiceId)
      else next.add(invoiceId)
      return next
    })
  }
}

function quoteLabel(quote: ReturnType<typeof useQuery<InvoiceSelectionQuoteResponse>>) {
  if (quote.isFetching || !quote.data?.quote) return 'Updating...'
  return quote.data.quote.amount
}
